import type { LucideIcon } from "lucide-react";
import { RevealGroup, RevealItem } from "@/components/motion/motion";
import { cn } from "@/lib/utils";

export type CardGridItem = {
	key: string;
	title: string;
	description?: string;
	icon?: LucideIcon;
	badge?: string;
	details?: { label: string; text: string }[];
};

const COLUMNS = {
	2: "md:grid-cols-2",
	3: "md:grid-cols-2 lg:grid-cols-3",
	4: "md:grid-cols-2 lg:grid-cols-4",
} as const;

/** Grid of short tiles; `numbered` renders an ordered list of steps. */
export function CardGrid({
	items,
	columns = 3,
	numbered = false,
	className,
}: {
	items: CardGridItem[];
	columns?: keyof typeof COLUMNS;
	numbered?: boolean;
	className?: string;
}) {
	return (
		<RevealGroup
			as={numbered ? "ol" : "ul"}
			className={cn("grid gap-4 md:gap-5", COLUMNS[columns], className)}
		>
			{items.map(
				({ key, title, description, icon: Icon, badge, details }, index) => (
					<RevealItem
						as="li"
						key={key}
						className="group relative flex flex-col rounded-2xl border bg-card p-6 transition-[border-color,transform] duration-300 ease-(--ease-soft) hover:-translate-y-0.5 hover:border-foreground/20 md:p-7"
					>
						{numbered || Icon || badge ? (
							<div className="mb-8 flex items-start justify-between gap-4">
								{numbered ? (
									<span
										aria-hidden="true"
										className="font-mono text-sm font-medium text-signal-ink tabular-nums"
									>
										{String(index + 1).padStart(2, "0")}
									</span>
								) : null}
								{Icon ? (
									<span
										aria-hidden="true"
										className="flex size-10 items-center justify-center rounded-xl border bg-background transition-colors group-hover:border-signal/50"
									>
										<Icon className="size-[1.1rem]" strokeWidth={1.6} />
									</span>
								) : null}
								{badge ? (
									<span className="rounded-full border px-2.5 py-0.5 font-mono text-[10px] tracking-[0.05em] text-muted-foreground uppercase">
										{badge}
									</span>
								) : null}
							</div>
						) : null}
						<h3 className="text-lg leading-snug font-semibold tracking-tight text-balance">
							{title}
						</h3>
						{description ? (
							<p className="mt-2 text-pretty text-muted-foreground">
								{description}
							</p>
						) : null}
						{details?.length ? (
							<dl className="mt-6 space-y-4 border-t pt-5 text-sm">
								{details.map((detail) => (
									<div key={detail.label}>
										<dt className="font-mono text-[11px] tracking-[0.06em] text-muted-foreground uppercase">
											{detail.label}
										</dt>
										<dd className="mt-1 text-pretty">{detail.text}</dd>
									</div>
								))}
							</dl>
						) : null}
					</RevealItem>
				),
			)}
		</RevealGroup>
	);
}

/** Maps a keyed dictionary group to card items, with optional icons per key. */
export const toCardItems = <
	T extends Record<string, { title: string; description?: string }>,
>(
	items: T,
	{
		icons,
		badge,
	}: { icons?: Partial<Record<keyof T, LucideIcon>>; badge?: string } = {},
): CardGridItem[] =>
	Object.entries(items).map(([key, item]) => ({
		key,
		title: item.title,
		description: item.description,
		icon: icons?.[key as keyof T],
		badge,
	}));
